// File: src/stores/main.ts
/**
 * @file Main Pinia store - TypeScript migration (MR1)
 *
 * This store was converted from JavaScript to TypeScript as part of the gradual
 * TypeScript migration initiative. Key changes:
 *
 * 1. **Full interface definitions** for state, organization, and user data
 * 2. **RoleSlug type integration** from @/config/roles for type-safe role handling
 * 3. **AuthzClaims type** for proper typing of authorization claims
 * 4. **Explicit return types** on all getters and actions
 *
 * Responsibilities:
 * - Holds user profile data (persisted to localStorage)
 * - Manages roles/permissions (NOT persisted for security)
 * - Constructs AuthzClaims for useAuthz() composable
 * - Handles auth flow (login state, logout)
 *
 * Security Notes:
 * - Only `userData` is persisted via pinia-plugin-persist
 * - roles/permissions/claims are fetched fresh on each session
 * - Token validation happens in loadUserData() action
 *
 * @module stores/main
 * @see {@link src/types/pinia-plugin-persist.d.ts} for persist plugin types
 */

import { defineStore } from 'pinia'
import { getUserData, getUserRoles, userLogout, updateUserProfile } from '@/services/UserService'
import { validateRoles, type RoleSlug } from '@/config/roles'
import type { AuthzClaims } from '@/composables/useAuthz'
import { processError } from '@/composables/utils'
import { announce } from '@/utils/announce'

/**
 * Supported organization types from backend TYPE_CHOICES.
 * @see Backend model for canonical definition
 */
export type OrganizationType =
    | 'msp' // Managed Service Provider
    | 'sme' // Small and Medium Enterprise
    | 'enterprise' // Enterprise
    | 'startup' // Startup
    | 'agency' // Agency
    | 'vendor' // Vendor
    | 'sole_proprietor' // Sole Proprietor
    | 'partnership' // Partnership
    | 'franchise' // Franchise
    | 'non_profit' // Non-Profit
    | 'government' // Government
    | 'manufacturer' // Manufacturer
    | 'holding_company' // Holding Company
    | 'subsidiary' // Subsidiary
    | 'personal' // Personal
    | 'internal' // Internal
    | 'partner' // Partner (legacy, retained for compatibility)

/**
 * Organization data structure from user profile.
 *
 * Note: `id` is typed as `number | string` because the backend may return either.
 * Components should use `.toString()` when passing to APIs that expect strings.
 */
export interface Organization {
    /** Organization ID - can be number or string depending on API endpoint */
    id: number | string
    /** Display name of the organization */
    name: string
    /** Organization type from backend TYPE_CHOICES */
    type?: OrganizationType
    /** Parent organization for hierarchical relationships (e.g., SME under MSP) */
    parent?: Organization | null
}

/**
 * User profile data returned from getUserData() API.
 * Extended with index signature for additional backend-provided fields.
 */
export interface UserData {
    id?: string
    username: string
    name?: string
    email?: string
    email_hash?: string
    url?: string
    organization?: Organization | null
    organizations?: Organization[]
    [key: string]: unknown
}

/**
 * Main store state interface.
 *
 * This interface defines the complete shape of the main store state,
 * enabling TypeScript to provide autocomplete and type checking throughout
 * the application when accessing store properties.
 */
export interface MainStoreState {
    /** Page header text (legacy, may be unused) */
    header: string | null
    /** Navigation items (legacy, may be unused) */
    navItems: unknown[]
    /** Global loading indicator */
    isLoading: boolean
    /** User profile data - PERSISTED to localStorage */
    userData: UserData | null
    /** User's role slugs from backend - NOT persisted for security */
    roles: RoleSlug[]
    /** Legacy single role field (deprecated, use roles array) */
    role: string
    /** Permission codenames from all user roles - NOT persisted for security */
    permissions: string[]
    /** Authorization claims for useAuthz() - NOT persisted for security */
    claims: AuthzClaims | null
    /** Mutex to prevent concurrent loadUserData() calls */
    isFetchingUserData: boolean
    /** True after successful loadUserData() completion */
    isUserDataLoaded: boolean
    /** Initial loading state before first loadUserData() attempt */
    isLoadingUserData: boolean
}

/**
 * Role data from getUserRoles API
 */
interface RoleFromApi {
    name: string
    permissions: Array<{ codename: string }>
}

/**
 * Safely parse userData from localStorage
 */
function parseStoredUserData(): UserData | null {
    const raw = localStorage.getItem('userData')
    if (raw && raw !== '' && raw !== 'undefined') {
        try {
            return JSON.parse(raw) as UserData
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e)
            return null
        }
    }
    return null
}

/**
 * Main application store.
 * Holds user profile, roles/permissions, and frontend auth claims for useAuthz().
 *
 * Security: we DO NOT persist roles/permissions/claims. Only 'userData' is persisted.
 */
export const useMainStore = defineStore('main', {
    state: (): MainStoreState => ({
        header: null,
        navItems: [],
        isLoading: false,

        // Restore persisted userData safely
        userData: parseStoredUserData(),

        // Legacy role/permission fields (some components may still read these)
        roles: [],
        role: '',
        permissions: [],

        /** Frontend authorization claims consumed by useAuthz (NOT persisted). */
        claims: null,

        isFetchingUserData: false,
        isUserDataLoaded: false,
        isLoadingUserData: true
    }),

    getters: {
        /** True if we have profile data (token presence is handled by services). */
        isAuthenticated: (state): boolean => !!state.userData,

        /** Current username */
        username: (state): string | undefined => state.userData?.username,

        /** Current user object (alias for userData) */
        user: (state): UserData | null => state.userData,

        /** Active organization (alias for organization) */
        activeOrganization: (state): Organization | null => {
            return state.userData?.organization || state.userData?.organizations?.[0] || null
        },

        /**
         * Current organization from user profile
         * Support both singular 'organization' and plural 'organizations' from API
         */
        organization: (state): Organization | null => {
            return state.userData?.organization || state.userData?.organizations?.[0] || null
        },

        /**
         * Legacy helper: check if user has any of the given role slugs.
         * Prefer useAuthz().hasAnyRole(...) in new code.
         */
        hasRole:
            (state) =>
            (maybeRoles: RoleSlug | RoleSlug[]): boolean => {
                const list = Array.isArray(maybeRoles) ? maybeRoles : [maybeRoles]
                return list.some((r) => state.roles.includes(r))
            },

        /** Legacy helper: check for a single permission. Prefer useAuthz().can(...). */
        hasPermission:
            (state) =>
            (perm: string): boolean =>
                state.permissions.includes(perm)
    },

    actions: {
        /** Legacy setter used by older flows. */
        setAuthenticated(status: boolean): void {
            this.userData = status ? { username: 'user@example.com' } : null
            localStorage.setItem('userData', JSON.stringify(this.userData))
        },

        /** Legacy setter used by older components. */
        setPermissions(perms: string[]): void {
            this.permissions = perms
        },

        /**
         * Load current user profile + roles/permissions from backend, then
         * construct sanitized, deduplicated claims for useAuthz().
         *
         * Deny-by-default: if token is missing, clear local session state and return early.
         */
        async loadUserData(): Promise<void> {
            if (this.isFetchingUserData) return

            const token = localStorage.getItem('authToken')
            if (!token) {
                this.clearSession()
                return
            }

            try {
                this.isFetchingUserData = true

                // 1) Profile
                const profile = await getUserData()
                this.userData = profile as UserData
                localStorage.setItem('userData', JSON.stringify(this.userData))

                // 2) Roles & permissions
                const userRoles = (await getUserRoles()) as RoleFromApi[]

                // Validate roles to filter out any unknown/invalid role strings from backend
                // This ensures this.roles and claims.roles stay consistent
                this.roles = validateRoles(userRoles.map((r) => r.name))
                this.role = this.roles.length === 1 ? this.roles[0] : ''

                // Flat-map and dedupe permission codenames across roles
                const perms = Array.from(
                    new Set(userRoles.flatMap((r) => r.permissions.map((p) => p.codename)))
                )

                this.permissions = perms

                // 3) Build frontend claims (sanitized roles, deduped perms, scoped)
                this.claims = {
                    roles: validateRoles(userRoles.map((r) => r.name)),
                    perms,
                    scope: {
                        organizationId: this.userData?.organization?.id?.toString() ?? null,
                        domainGroupId: null,
                        domainId: null
                    }
                }

                this.isUserDataLoaded = true
            } catch (error: unknown) {
                const axiosError = error as {
                    response?: { status: number; data: unknown }
                    message?: string
                }
                const message =
                    axiosError?.response?.status === 403
                        ? 'Your session has expired. Please log in again.'
                        : processError(
                              axiosError,
                              'Failed to fetch user data. Please log in again.'
                          )

                announce(message, 'error', {
                    scope: 'AUTH',
                    extra: {
                        error: axiosError
                    }
                })
                this.clearSession()
                throw new Error(message)
            } finally {
                this.isFetchingUserData = false
                this.isLoadingUserData = false
            }
        },

        /**
         * Persists a partial update to the authenticated user's profile and
         * reflects the changes locally in the store and localStorage.
         * @param {Partial<UserData>} data - The fields to update (e.g. `{ name: 'Alice' }`).
         */
        async updateUserData(data: Partial<UserData>): Promise<void> {
            const urlParts = this.userData?.url?.split('/').filter(Boolean)
            const userId = this.userData?.id ?? urlParts?.[urlParts.length - 1]
            if (!userId) throw new Error('User ID is not available.')
            const updated = await updateUserProfile(userId, data)
            this.userData = { ...this.userData, ...updated }
            localStorage.setItem('userData', JSON.stringify(this.userData))
        },

        /** Legacy: allow user to switch currently selected role (UI only). */
        setCurrentRole(role: RoleSlug): void {
            if (this.roles.includes(role)) this.role = role
        },

        /**
         * Clears local authentication/session state without user-facing success messaging.
         * Used for token expiry, bootstrap failures, and other recovery paths.
         */
        clearSession(): void {
            localStorage.removeItem('isAuthenticated')
            localStorage.removeItem('authToken')
            localStorage.removeItem('userData')
            this.$reset()
        },

        /**
         * Logout flow:
         * - server logout (best-effort)
         * - clear local session state
         * - reset store state
         *
         * Navigation is intentionally NOT performed here. Callers are responsible
         * for redirecting to /login so that router guards and router.isReady()
         * remain the single source of truth for navigation decisions.
         */
        async logout(): Promise<void> {
            let logoutHadServerError = false
            try {
                await userLogout()
            } catch (error: unknown) {
                const axiosError = error as { response?: { status?: number }; message?: string }
                if (axiosError?.response?.status === 403) {
                    // already invalid; ignore
                } else {
                    logoutHadServerError = true
                }
            }
            this.clearSession()
            if (logoutHadServerError) {
                announce('Logged out locally, but the server logout request failed.', 'warning', {
                    scope: 'AUTH'
                })
            } else {
                announce('Logged out successfully.', 'info', { scope: 'AUTH' })
            }
        }
    },

    // Security: do NOT persist claims/roles/permissions
    persist: {
        paths: ['userData'] // exclude claims, roles, permissions
    }
})

/**
 * Type helper for accessing the store outside of setup()
 */
export type MainStore = ReturnType<typeof useMainStore>
